import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight, Images } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { StatusBadge } from "@/components/StatusBadge";

const TABS = ["All", "Ongoing", "Completed"];

export default function Projects() {
  useSeo("projects");
  const [projects, setProjects] = useState([]);
  const [tab, setTab] = useState("All");

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);
  const filtered = projects.filter((p) => tab === "All" || p.status === tab);

  return (
    <div>
      <section className="bg-navy py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-teal mb-3">Portfolio</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white">Our Projects</h1>
          <p className="mt-4 text-white/70 max-w-xl">Premium residential and interior developments across Chennai.</p>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="flex gap-2 mb-10">
            {TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`projects-filter-${t.toLowerCase()}`}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                  tab === t ? "bg-navy text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}>
                {t}
              </button>
            ))}
          </div>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.1}>
                <Link to={`/projects/${p.id}`} className="group block overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-100 hover:shadow-xl transition-shadow" data-testid={`project-card-${p.id}`}>
                  <div className="relative h-60 overflow-hidden">
                    <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute top-3 left-3"><StatusBadge status={p.status} /></div>
                    {(p.images?.length > 0) && (
                      <div className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/55 px-2.5 py-1 text-xs font-semibold text-white">
                        <Images className="h-3.5 w-3.5" /> {([p.image, ...(p.images || [])].filter(Boolean)).length}
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-xl font-semibold text-slate-900">{p.name}</h3>
                    <p className="mt-1 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {p.location}</p>
                    <p className="mt-3 text-sm text-slate-600">{p.type}</p>
                    {p.status === "Ongoing" && (
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                          <span>Progress</span><span className="font-semibold text-teal-dark">{p.progress_percentage}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full bg-teal transition-all" style={{ width: `${p.progress_percentage}%` }} />
                        </div>
                      </div>
                    )}
                    <span data-testid={`project-details-${p.id}`}
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-navy group-hover:text-teal transition-colors">
                      View Details <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          {filtered.length === 0 && <p className="text-center text-slate-400 py-20">No projects in this category yet.</p>}
        </div>
      </section>
    </div>
  );
}
